import {inject, Injectable} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {UserService} from '../../api';
import {QueryKey} from '../constants/query-key';

export interface UpdateProfileInput {
  firstName?: string | null;
  lastName?: string | null;
  localeIsoCode: string;
  avatar?: File | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileCommandService {
  private readonly userService = inject(UserService);
  private readonly queryClient = inject(QueryClient);

  updateProfileMutation = injectMutation(() => ({
    mutationFn: async (input: UpdateProfileInput) => {
      await lastValueFrom(this.userService.updateProfile({
        firstName: input.firstName,
        lastName: input.lastName,
        localeIsoCode: input.localeIsoCode,
      }));

      // The avatar now has its own endpoint, upload it only when a new file was picked.
      if (input.avatar) {
        await lastValueFrom(this.userService.uploadAvatar(input.avatar));
      }
    },
    // Profile and avatar are two separate requests: if the avatar upload fails after the profile
    // update succeeded, we still refetch so the persisted name/locale (and any new avatar URL) show up.
    onSettled: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CURRENT_USER]});
    },
  }));
}
